import React from "react";
import { DeleteOutlined, CalendarOutlined  } from "@ant-design/icons";
import { 
	Row,
	Col,
	Select,
	Form,
	Input,
	Tooltip,
	theme,
	InputNumber,
	Popover,
	Space,
	Button
} from "antd";
import PropTypes from "prop-types";

import { TaxSelector } from "./TaxSelector";

const { TextArea } = Input;

export const InvoiceLine = ({field, company, handleFieldChange, form, changes, remove, exchangeRate}) => {
	
	const {token:antToken} = theme.useToken();

	// Función para agregar "{month/year}" al final de la descripción
	const appendMonthYearPlaceholder = (option = "") => {
		// Obtén el valor actual del campo de descripción específico usando [field.name, "description"]
		const currentDescription = form.getFieldValue(["items", field.name, "description"]);

		const dateToSet = {
			"-1": "{month-1/year}",
			"+1": "{month+1/year}",
		};
		
		const selectedDate = dateToSet[option] ?? "{month/year}";
		
		// Verifica si ya tiene "{month/year}" y, si no, lo agrega
		const newDescription = currentDescription?.includes(selectedDate)
			? currentDescription
			: `${currentDescription || ""} ${selectedDate}`;

		// Actualiza el valor en el formulario para ese campo específico
		form.setFieldsValue({
			items: {
				...form.getFieldValue("items"), // Mantenemos los otros campos sin cambios
				[field.name]: {
					...form.getFieldValue(["items", field.name]), // Mantenemos los otros valores de este item
					description: newDescription, // Actualizamos solo la descripción
				},
			},
		});
	};

	return (
		<>
			<Row 
				gutter={[10, 0]}
				style={{
					borderBottom: `2px solid ${antToken.colorBorder}`,
					marginBottom: 10
				}}
			>
				<Col 
					span={20}
				>
					<Row 
						gutter={[10, 0]}
					>
						<Col span={6}>							
							<Form.Item
								{...field}
								name={[field.name, "description"]}
								rules={[{ required: true, message: "Por favor ingrese la descripción" }]}
								extra={
									<Col>
										<Tooltip title="Eliminar">
											<DeleteOutlined
												style={{
													color:"red"
												}}
												onClick={() => {
													remove(field.name);
													handleFieldChange("refresh", "", "");
												}}
											/>
										</Tooltip>
										<Popover placement="rightBottom" content={() => (
											<Space swap>
												<Button type="" onClick={() => appendMonthYearPlaceholder("-1")}>Mes Anterior</Button>
												<Button type="primary" onClick={appendMonthYearPlaceholder}>Mes Actual</Button>
												<Button type="" onClick={() => appendMonthYearPlaceholder("+1")}>Mes Siguiente</Button>
											</Space>
										)} title="Agregar mes y año automáticamente en la descripción">
											<CalendarOutlined
												style={{
													marginLeft:"50%",
													color: antToken.colorPrimary,
												}}												
											/>
										</Popover>
									</Col>
								}								
							>
								<TextArea size="small" rows={3} placeholder="Descripción" />
							</Form.Item>
						</Col>
						<Col span={17}>
							<Row gutter={[10, 0]}>
								<Col span={5}>
									<Form.Item
										{...field}
										name={[field.name, "type"]}
										rules={[{ required: true, message: "Por favor ingrese el tipo" }]}
										
									>
										<Select
											style={{ width: "100%" }}
											placeholder="Tipo"
											optionFilterProp="label"
											options={[
												{ value: "B", label: "Bien" },
												{ value: "S", label: "Servicio" },
											]}
										/>
									</Form.Item>
								</Col>
								<Col span={5}>
									<Form.Item
										{...field}
										name={[field.name, "quantity"]}
										rules={[{ required: true, message: "Por favor ingrese la Cantidad" }]}
									>
										<InputNumber
											placeholder="Cantidad" 
											style={{ width: "100%" }}
											min={0}
											onChange={(value) => handleFieldChange(field.name, "quantity", value)}
											precision={2} // Cambiar de 0 a 2 para permitir hasta 2 decimales
											controls={false}
										/>
									</Form.Item>
								</Col>
								<Col span={8}>
									<Form.Item
										{...field}
										name={[field.name, "priceUnit"]}
										rules={[{ required: true, message: "Por favor ingrese el precio" }]}
									>
										<InputNumber 
											placeholder="Precio Unitario"											
											min={0}
											onChange={(value) => handleFieldChange(field.name, "priceUnit", value)} 
											style={{ width: "100%" }}
											precision={2}
											controls={false}
										/>
									</Form.Item>
								</Col>
								<Col span={6}>
									<Form.Item
										{...field}
										name={[field.name, "discount"]}
										rules={[{ required: false }]}
									>
										<InputNumber
											placeholder="Descuento"
											min={0}
											onChange={(value) => handleFieldChange(field.name, "discount", value)}
											style={{ width: "100%" }}
											precision={2}
											controls={false}
										/>
									</Form.Item>
								</Col>
								<TaxSelector 
									field={field}
									form={form}
									company={company}
									changes={changes}
									handleFieldChange={handleFieldChange}
									exchangeRate={exchangeRate}
								/>
								<Col
									span={12}
								>
									<Form.Item
										{...field}
										name={[field.name, "accounting_account"]}
										rules={[{ required: false }]}
									>
										<Select
											style={{ width: "100%" }}
											placeholder="Cuenta Contable"
											optionFilterProp="label"
											options={
												company.accounting_resources.accounting_plan && company.accounting_resources.sale_accounts
													? company.accounting_resources.accounting_plan
														.filter((element) =>
															company.accounting_resources.sale_accounts.includes(
																element.id
															)
														)
														.map((element) => ({
															label: element.display_name,
															value: String(element.id),
														}))
													: []
											}
											onChange={(e) => handleFieldChange(field.name, "accounting_account", e)}
										/>
									</Form.Item> 
								</Col>
							</Row>
						</Col>
					</Row>
				</Col>
				<Col span={4}>
					<Form.Item
						{...field}
						name={[field.name, "calculus"]}
						rules={[{ required: false }]}
					>
						<div style={{ fontFamily: "monospace", lineHeight: "1.1", textAlign: "right" }}>
							<div dangerouslySetInnerHTML={{ __html: form.getFieldValue(["items", field.name, "calculusHTML"]) }} />
						</div>
					</Form.Item>
				</Col>
			</Row>
		</>
	);
};

InvoiceLine.propTypes = {	
	field: PropTypes.shape({
		name: PropTypes.number.isRequired,
	}).isRequired,
	company: PropTypes.shape({
		accounting_resources: PropTypes.shape({
			accounting_plan: PropTypes.arrayOf(PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
				display_name: PropTypes.string.isRequired,
			})).isRequired,
			sale_accounts: PropTypes.arrayOf(
				PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
			).isRequired,
		}).isRequired,
	}).isRequired,
	handleFieldChange: PropTypes.func.isRequired,
	form: PropTypes.any.isRequired,
	changes: PropTypes.number, // You can define the shape of this object based on your needs
	remove: PropTypes.func.isRequired,
	exchangeRate: PropTypes.number.isRequired,
};
